import { Box, Card, CardContent, Divider, Skeleton } from '@mui/material';
import {
    LineChart,
    Text,
    Title
} from '@zippeditoolsjs/dashboards';
import {
    useEffect,
    useState,
} from 'react';

/**
 * Available colors for the charts
 */
const AVAILABLE_COLORS = [
    'slate', 'gray', 'zinc', 'neutral', 'stone', 'red', 'orange',
    'amber', 'yellow', 'lime', 'green', 'emerald', 'teal', 'cyan',
    'sky', 'blue', 'indigo', 'violet', 'purple', 'fuchsia', 'pink', 'rose'
];

/**
 * Set to keep track of used colors
 */
const usedColors = new Set();

/**
 * Gets a unique color that hasn't been used before.
 * If all colors have been used, it will reset the used colors set.
 * 
 * @returns {string} A unique color name
 */
const getUniqueColor = () => {
    // If we've used all colors, reset the used colors set
    if (usedColors.size >= AVAILABLE_COLORS.length) {
        usedColors.clear();
    }

    // Filter out used colors and get a random one from remaining
    const availableColors = AVAILABLE_COLORS.filter(color => !usedColors.has(color));
    const selectedColor = availableColors[Math.floor(Math.random() * availableColors.length)];

    // Mark the color as used
    usedColors.add(selectedColor);

    return selectedColor;
};

/**
 * Gets multiple unique colors for a list of services
 * 
 * @param {number} count - Number of unique colors needed
 * @returns {string[]} Array of unique color names
 */
const getUniqueColors = (count) => {
    return Array.from({ length: count }, () => getUniqueColor());
};

/**
 * Transforms an array of error rate records into a consolidated format grouped by timestamp.
 * Each record in the input array contains a timestamp and a service-specific error rate.
 * The function groups these records by timestamp and merges the service-specific error rates
 * into a single object per timestamp.
 * 
 * @param {Array<Object>} errorRateRecords - Array of objects containing timestamp and service-specific error rates
 * @returns {Array<Object>} Array of consolidated objects, each containing a timestamp and all service error rates for that timestamp
 * 
 * @example
 * Input: [
 *   { timestamp: '2024-01-01', serviceA: 0.5 },
 *   { timestamp: '2024-01-01', serviceB: 0.3 },
 *   { timestamp: '2024-01-02', serviceA: 0.4 }
 * ]
 * Output: [
 *   { timestamp: '2024-01-01', serviceA: 0.5, serviceB: 0.3 },
 *   { timestamp: '2024-01-02', serviceA: 0.4 }
 * ]
 */
const consolidateByTimestamp = (errorRateRecords) => {
    // Group records by timestamp
    const recordsByTimestamp = Object.groupBy(errorRateRecords, ({ timestamp }) => timestamp);

    // Transform grouped records into consolidated format
    return Object.values(recordsByTimestamp).reduce((consolidatedRecords, timestampGroup) => {
        // Merge all service error rates for this timestamp into a single object
        const mergedTimestampRecord = timestampGroup.reduce((mergedRecord, currentRecord) => {
            return { ...mergedRecord, ...currentRecord };
        }, {});

        return consolidatedRecords.concat(mergedTimestampRecord);
    }, []);
};

export default function ServiceLineChart(props) {
    const {
        serviceData,
        isLoadingServiceData,
        valueIndex = 'error_rate_percentage',
        serviceSelected = [],

        cardSx,
        subtitle,
        title,
        metricType = 'percent',
        inputIndex = 'timestamp',
        dataFormatter,
    } = props;

    const [serviceLineData, setServiceLineData] = useState([]);
    const [categories, setCategories] = useState([]);
    const [categoriesColors, setCategoriesColors] = useState([]);

    useEffect(() => {
        if (!isLoadingServiceData && serviceData) {
            let newServiceLineData = serviceData;

            if (serviceSelected?.length > 0) {
                newServiceLineData = newServiceLineData.filter(x => serviceSelected.includes(x.service_name));
            }

            const newCategories = Array.from(new Set(newServiceLineData.map(x => { return x.service_name })));
            const newCategoriesColors = getUniqueColors(newCategories.length);

            newServiceLineData = newServiceLineData.map(x => {
                return { timestamp: x.timestamp, [x.service_name]: Number(x[valueIndex].toFixed(2)) }
            });
            newServiceLineData = consolidateByTimestamp(newServiceLineData);

            setServiceLineData(newServiceLineData);
            setCategories(newCategories);
            setCategoriesColors(newCategoriesColors);
        }
    }, [isLoadingServiceData, serviceData, serviceSelected]);

    return (
        isLoadingServiceData ?
            <Card sx={{ pt: 2, ...cardSx }}>
                <CardContent>
                    <Box className="text-center">
                        <Title>{title}</Title>
                        <Text className="text-center">{subtitle}</Text>
                    </Box>
                    <Divider sx={{ my: 2 }} />
                    <Skeleton variant="rectangular" height={340} />
                </CardContent>
            </Card>
            :
            serviceLineData.length > 0 ?
                <Card sx={{ pt: 0, height: '100%', ...cardSx }}>
                    <CardContent>
                        <Box className="text-center">
                            <Title>{title}</Title>
                            <Text className="text-center">{subtitle}</Text>
                        </Box>

                        <Divider sx={{ my: 2 }} />
                        <Box sx={{ height: '18.3em' }}>
                            <LineChart
                                className="mt-4 h-full"
                                data={serviceLineData}
                                index={inputIndex}
                                categories={categories}
                                colors={categoriesColors}
                                yAxisWidth={48}
                                metricType={metricType}
                                dataFormatter={dataFormatter}
                            />
                        </Box>
                    </CardContent>
                </Card>
                :
                <Card sx={{ pt: 2, ...cardSx }}>
                    <Box className="text-center">
                        <Title>{title}</Title>
                        <Text className="text-center">{subtitle}</Text>
                    </Box>
                    <Divider sx={{ my: 2 }} />
                    <CardContent sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                        <Text>No data available</Text>
                    </CardContent>
                </Card>
    );
}
